import { useMemo } from "react";

// A previewed transaction row (subset of TransactionRow we render in buckets).
export type DeltaRow = {
  readonly txn: { readonly id: string };
  readonly postingDate: string;
  readonly description: string;
  readonly amount: number;
  readonly accountNumber: string | null | undefined;
  readonly expenseNature: { readonly id: string; readonly name: string } | null | undefined;
};

export type Delta = {
  readonly capturedCount: number;
  readonly droppedCount: number;
  readonly manualConflictCount: number;
  readonly truncated: boolean;
  readonly captured: ReadonlyArray<DeltaRow>;
  readonly droppedRuleAssigned: ReadonlyArray<DeltaRow>;
  readonly droppedManual: ReadonlyArray<DeltaRow>;
};

const ROW_CAP = 50;

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

function Bucket({
  title,
  tone,
  rows,
}: {
  title: string;
  tone: string;
  rows: ReadonlyArray<DeltaRow>;
}) {
  if (rows.length === 0) return null;
  const shown = rows.slice(0, ROW_CAP);
  const more = rows.length - shown.length;
  return (
    <div>
      <p className={`text-xs font-semibold uppercase mb-1 ${tone}`}>
        {title} ({rows.length.toLocaleString()})
      </p>
      <div className="max-h-40 overflow-y-auto rounded border divide-y">
        {shown.map((r) => (
          <div
            key={r.txn.id}
            className="flex items-center gap-2 px-2 py-1 text-xs"
          >
            <span className="text-gray-400 whitespace-nowrap">
              {r.postingDate}
            </span>
            <span className="truncate flex-1" title={r.description}>
              {r.description}
            </span>
            {r.expenseNature && (
              <span className="text-indigo-700 whitespace-nowrap">
                {r.expenseNature.name}
              </span>
            )}
            <span className="text-gray-500 whitespace-nowrap">
              {money(r.amount)}
            </span>
          </div>
        ))}
      </div>
      {more > 0 && (
        <p className="text-xs text-gray-400 mt-0.5">…and {more.toLocaleString()} more</p>
      )}
    </div>
  );
}

// The entering/leaving body of a previewed change: the captured/dropped summary,
// the three transaction buckets, and the override-manual checkbox. Shared by the
// confirm dialog (RecaptureDialog) and the rich edit dialog (EditRuleDialog) so
// the impact preview reads identically wherever a rule change is shown.
export function DeltaPreview({
  delta,
  loading,
  overrideManual,
  onToggleOverride,
}: {
  delta: Delta | null;
  loading: boolean;
  overrideManual: boolean;
  onToggleOverride: (next: boolean) => void;
}) {
  if (loading || !delta)
    return <p className="text-sm text-gray-400 py-6">Previewing impact…</p>;

  const zeroDelta = delta.capturedCount === 0 && delta.droppedCount === 0;
  if (zeroDelta)
    return <p className="text-sm text-gray-600 py-4">No transactions affected.</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        <span className="font-semibold text-emerald-700">
          {delta.capturedCount.toLocaleString()}
        </span>{" "}
        entering ·{" "}
        <span className="font-semibold text-orange-700">
          {delta.droppedCount.toLocaleString()}
        </span>{" "}
        leaving
        {delta.truncated && (
          <span className="text-gray-400">
            {" "}
            — preview capped, counts are a lower bound
          </span>
        )}
      </p>

      <Bucket title="Entering" tone="text-emerald-700" rows={delta.captured} />
      <Bucket
        title="Leaving (this rule)"
        tone="text-orange-700"
        rows={delta.droppedRuleAssigned}
      />
      <Bucket title="Leaving (manual)" tone="text-red-700" rows={delta.droppedManual} />

      {delta.manualConflictCount > 0 && (
        <label className="flex items-start gap-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-2">
          <input
            type="checkbox"
            checked={overrideManual}
            onChange={(e) => onToggleOverride(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Override {delta.manualConflictCount.toLocaleString()} manually assigned
            transaction
            {delta.manualConflictCount === 1 ? "" : "s"} too. By default manual
            assignments are kept.
          </span>
        </label>
      )}
    </div>
  );
}

// Reusable confirm dialog for any rule change that has a previewable delta
// (exclude, carve, recapture, delete). The parent owns fetching the preview and
// running the confirm mutation. A zero-delta change confirms with a "No
// transactions affected" notice. (Rich edit and merge have their own dialogs.)
export default function RecaptureDialog({
  title,
  confirmLabel,
  delta,
  loading,
  saving,
  error,
  overrideManual,
  onToggleOverride,
  onConfirm,
  onCancel,
}: {
  title: string;
  confirmLabel: string;
  delta: Delta | null;
  loading: boolean;
  saving: boolean;
  error?: string | null;
  // When true, droppedManual rows are added to overrideTxnIds on confirm.
  overrideManual: boolean;
  onToggleOverride: (next: boolean) => void;
  onConfirm: (overrideTxnIds: string[]) => void;
  onCancel: () => void;
}) {
  const overrideTxnIds = useMemo(
    () =>
      overrideManual && delta ? delta.droppedManual.map((r) => r.txn.id) : [],
    [overrideManual, delta]
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-[640px] max-h-[85vh] overflow-y-auto p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <DeltaPreview
          delta={delta}
          loading={loading}
          overrideManual={overrideManual}
          onToggleOverride={onToggleOverride}
        />

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            disabled={saving || loading || !delta}
            onClick={() => onConfirm(overrideTxnIds)}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {saving ? "Applying…" : confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
